import { useCallback, useEffect, useRef, useState } from 'react';
import type { V2ToastItem } from '@sakhtar/design-system/react';

type ToastTone = NonNullable<V2ToastItem['tone']>;

const TOAST_MS = 5000;

/**
 * Toasts confirm a finished action only; errors stay on the page in a V2Alert
 * so they can be read again (docs/design-system/V2_USAGE.md §1a).
 */
export function useToasts() {
  const [toasts, setToasts] = useState<readonly V2ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone = 'success') => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message, tone }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), TOAST_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  return { toasts, push, dismiss };
}

/** A value that follows `value` after `delayMs` of quiet — keeps a search box from firing a request per keystroke. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
